import React, { useEffect, useState } from 'react';
import { Copy, Download, Loader2, Send, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const PLATEFORMES = [['instagram', 'Instagram Reels'], ['facebook', 'Facebook'], ['youtube', 'YouTube Shorts']];
const EN_COURS = ['en_attente', 'script', 'tournage'];

function Lecteur({ v }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!v.chemin) return undefined;
    let lien;
    api.get(`/videos-virales/${v.id}/fichier`, { responseType: 'blob' }).then((r) => { lien = URL.createObjectURL(r.data); setUrl(lien); }).catch(() => {});
    return () => lien && URL.revokeObjectURL(lien);
  }, [v.id, v.chemin]);
  if (EN_COURS.includes(v.statut)) {
    return (
      <div className="aspect-[9/16] w-full rounded-xl bg-stone-900 flex flex-col items-center justify-center gap-3 text-stone-200 p-4 text-center" data-testid={`vv-progression-${v.id}`}>
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" /><p className="text-sm">{v.etape}</p>
      </div>
    );
  }
  if (v.statut === 'erreur') {
    return <div className="aspect-[9/16] w-full rounded-xl bg-red-50 flex flex-col items-center justify-center gap-2 p-4 text-center text-red-700 text-sm" data-testid={`vv-erreur-${v.id}`}><AlertTriangle className="w-6 h-6" />{v.etape}</div>;
  }
  return url
    ? <video src={url} controls playsInline className="aspect-[9/16] w-full rounded-xl bg-black object-cover" data-testid={`vv-lecteur-${v.id}`} />
    : <div className="aspect-[9/16] w-full rounded-xl bg-stone-100 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
}

function Publication({ v, connexions, onFait }) {
  const [choix, setChoix] = useState(['instagram', 'facebook', 'youtube']);
  const [envoi, setEnvoi] = useState(false);
  const ok = { instagram: connexions?.instagram, facebook: connexions?.facebook, youtube: (connexions?.youtube || []).length > 0 };
  const publier = async () => {
    setEnvoi(true);
    try {
      const r = await api.post(`/videos-virales/${v.id}/publier`, { plateformes: choix });
      Object.entries(r.data.publications).forEach(([p, x]) => (x.ok ? toast.success(`Publié sur ${p}`) : toast.error(`${p} : ${x.erreur}`)));
      onFait();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Publication impossible'); }
    setEnvoi(false);
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" data-testid={`vv-publier-${v.id}`}><Send className="w-4 h-4 mr-1" />Publier</Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="vv-dialog-publier">
        <AlertDialogHeader>
          <AlertDialogTitle>Publier cette vidéo ?</AlertDialogTitle>
          <AlertDialogDescription>Elle sera publiée publiquement avec la légende et les hashtags proposés.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          {PLATEFORMES.map(([p, l]) => (
            <label key={p} className="flex items-center gap-2 text-sm">
              <Checkbox checked={choix.includes(p)} onCheckedChange={(c) => setChoix(c ? [...choix, p] : choix.filter((x) => x !== p))} data-testid={`vv-choix-${p}`} />
              {l}{!ok[p] && <span className="text-xs text-amber-600">(compte non connecté)</span>}
            </label>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="vv-publier-annuler">Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={publier} disabled={!choix.length || envoi} data-testid="vv-publier-confirmer">Oui, publier</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function CarteVideo({ v, connexions, onMaj }) {
  const texte = `${v.legende || ''}\n\n${(v.hashtags || []).map((h) => `#${h}`).join(' ')}`;
  const telecharger = async () => {
    const id = toast.loading('Téléchargement de la vidéo…');
    try {
      const r = await api.get(`/videos-virales/${v.id}/fichier`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = `video-${v.id}.mp4`; a.click();
      toast.success('Vidéo téléchargée', { id });
    } catch { toast.error('Téléchargement impossible', { id }); }
  };
  const supprimer = async () => { await api.delete(`/videos-virales/${v.id}`); onMaj(); };
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-3 space-y-3" data-testid={`vv-carte-${v.id}`}>
      <Lecteur v={v} />
      <div className="space-y-1">
        <p className="font-semibold text-sm leading-snug" data-testid={`vv-titre-${v.id}`}>{v.titre_youtube || v.sujet}</p>
        {v.texte_ecran && <p className="text-xs text-stone-500">Accroche : « {v.texte_ecran} »</p>}
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline">{v.duree} s</Badge>
          {Object.entries(v.publications || {}).map(([p, x]) => (
            x.ok
              ? <a key={p} href={x.lien} target="_blank" rel="noreferrer"><Badge className="bg-emerald-600"><CheckCircle2 className="w-3 h-3 mr-1" />{p}</Badge></a>
              : <Badge key={p} variant="destructive" title={x.erreur}>{p}</Badge>
          ))}
        </div>
      </div>
      {v.legende && <p className="text-xs text-stone-700 whitespace-pre-line bg-stone-50 rounded-lg p-2 max-h-28 overflow-y-auto" data-testid={`vv-legende-${v.id}`}>{texte}</p>}
      <div className="flex flex-wrap gap-2">
        {v.chemin && <Button size="sm" variant="outline" onClick={telecharger} data-testid={`vv-telecharger-${v.id}`}><Download className="w-4 h-4 mr-1" />Télécharger</Button>}
        {v.legende && <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(texte); toast.success('Légende copiée'); }} data-testid={`vv-copier-${v.id}`}><Copy className="w-4 h-4 mr-1" />Légende</Button>}
        {v.chemin && <Publication v={v} connexions={connexions} onFait={onMaj} />}
        <Button size="sm" variant="ghost" onClick={supprimer} disabled={EN_COURS.includes(v.statut)} data-testid={`vv-supprimer-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
